"use client"

import { useState, useRef, useEffect, useCallback, type ReactNode, type RefObject } from "react"
import { createPortal } from "react-dom"
import { Clock, Smile, Hand, Heart, Leaf, Gamepad2, Sparkles } from "lucide-react"

/* ── Emoji 分类数据 ── */

interface EmojiCategory {
    label: string
    icon: ReactNode
    emojis: string[]
}

const EMOJI_CATEGORIES: EmojiCategory[] = [
    {
        label: "常用",
        icon: <Clock className="h-4 w-4" />,
        emojis: [
            "😀", "😂", "🤣", "😊", "😍", "🥰", "😘", "😎",
            "🤔", "😏", "😅", "😢", "😭", "😤", "🥺", "😱",
            "👍", "👎", "👏", "🙌", "🤝", "💪", "❤️", "🔥",
            "⭐", "🎉", "🎊", "💯", "✅", "❌", "💡", "🦞",
        ],
    },
    {
        label: "表情",
        icon: <Smile className="h-4 w-4" />,
        emojis: [
            "😀", "😃", "😄", "😁", "😆", "😅", "🤣", "😂",
            "🙂", "🙃", "😉", "😊", "😇", "🥰", "😍", "🤩",
            "😘", "😗", "😚", "😙", "😢", "😋", "😛", "😜",
            "🤪", "😝", "🤑", "🤗", "🤭", "🤫", "🤔", "😅",
            "🤐", "🤨", "😐", "😑", "😶", "😣", "😏", "😒",
            "🙄", "😬", "🤥", "😌", "😔", "😪", "🤤", "😴",
            "😷", "🤒", "🤕", "🤢", "🤮", "🥵", "🥶", "🥴",
            "😵", "🤯", "🤠", "🥳", "😜", "😎", "🤓", "🧐",
        ],
    },
    {
        label: "手势",
        icon: <Hand className="h-4 w-4" />,
        emojis: [
            "👋", "🤚", "🖐️", "✋", "🖖", "👋", "🤚", "✋",
            "🖖", "👌", "🤏", "✌️", "🤞", "🤙", "🤟",
            "🤘", "🤙", "👈", "👉", "👆", "🖕", "👇", "☝️",
            "👆", "👍", "👎", "✊", "👊", "🤛", "🤜", "👏",
            "🙌", "🤲", "👐", "🤲", "🤝", "🙏", "💪", "🦾", "🦿",
        ],
    },
    {
        label: "爱心",
        icon: <Heart className="h-4 w-4" />,
        emojis: [
            "❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍",
            "🤎", "💔", "💘", "💝", "❣️", "💕", "💞", "💓",
            "💗", "💖", "💘", "💝", "💟", "♥️", "💑", "😍",
            "🥰", "😘", "💋", "💑", "👩‍❤️‍👨", "💏", "👫", "🤗",
        ],
    },
    {
        label: "自然",
        icon: <Leaf className="h-4 w-4" />,
        emojis: [
            "🌸", "💐", "🌹", "🌺", "🌻", "🌼", "🌷", "🌻",
            "🌱", "🌿", "🌲", "🌳", "🌴", "🌵", "🌿", "☘️",
            "🍀", "🍁", "🍂", "🍃", "🌾", "🌻", "🍄", "🌾",
            "🐶", "🐱", "🐭", "🐹", "🐰", "🦊", "🐻", "🐼",
            "🦞", "🦐", "🐙", "🐚", "🐬", "🐳", "🦈", "🐠",
        ],
    },
    {
        label: "物品",
        icon: <Gamepad2 className="h-4 w-4" />,
        emojis: [
            "⚽", "🏀", "🏈", "⚾", "🥎", "🎾", "🏐", "🏉",
            "🎮", "🕹️", "🎲", "🧩", "♟️", "🎯", "🎳", "🎪",
            "🎨", "🎬", "🎤", "🎧", "🎼", "🎹", "🥁", "🎷",
            "💻", "🖥️", "📱", "⌨️", "🖱️", "💾", "📷", "🔭",
            "💡", "🔋", "🔌", "📡", "🛠️", "🔧", "🔨", "⚙️",
        ],
    },
    {
        label: "符号",
        icon: <Sparkles className="h-4 w-4" />,
        emojis: [
            "✨", "🌟", "💫", "⭐", "🔥", "💥", "⚡", "🌈",
            "☀️", "🌤️", "⛅", "🌦️", "🌧️", "⛈️", "❄️", "💨",
            "🎉", "🎊", "🎈", "🎁", "🏆", "🥇", "🥈", "🥉",
            "💯", "✅", "❌", "⭕", "❗", "❓", "💬", "💭",
            "🔔", "🔕", "📢", "📣", "🏳️", "🏴", "🚩", "🎌",
        ],
    },
]

/* ── Props ── */

interface EmojiPickerProps {
    /** 选中 emoji 回调 */
    onSelect: (emoji: string) => void
    /** 是否显示 */
    open: boolean
    /** 关闭回调 */
    onClose: () => void
    /** 触发按钮 ref，点击该元素时不触发外部关闭 */
    triggerRef?: React.RefObject<HTMLButtonElement | null>
}

/**
 * EmojiPicker — 通过 Portal 渲染到 body，使用 fixed 定位，
 * 避免被任何父级 overflow 截断。
 */
export function EmojiPicker({ onSelect, open, onClose, triggerRef }: EmojiPickerProps) {
    const [activeCategory, setActiveCategory] = useState(0)
    const panelRef = useRef<HTMLDivElement>(null)
    const [pos, setPos] = useState<{ top: number; left: number; flip: boolean } | null>(null)

    // 面板预估高度（分类栏 + emoji 网格 + 内边距）
    const PANEL_HEIGHT = 350
    const GAP = 8

    // 计算面板位置：基于 triggerRef 按钮，自动判断上下方向
    useEffect(() => {
        if (!open || !triggerRef?.current) {
            setPos(null)
            return
        }
        function update() {
            const rect = triggerRef!.current!.getBoundingClientRect()
            const spaceBelow = window.innerHeight - rect.bottom
            const flip = spaceBelow < PANEL_HEIGHT + GAP
            setPos({
                top: flip ? rect.top - GAP : rect.bottom + GAP,
                left: rect.left,
                flip,
            })
        }
        update()
        window.addEventListener("scroll", update, true)
        window.addEventListener("resize", update)
        return () => {
            window.removeEventListener("scroll", update, true)
            window.removeEventListener("resize", update)
        }
    }, [open, triggerRef])

    // 点击外部关闭（排除触发按钮，由按钮自身的 toggle 处理）
    useEffect(() => {
        if (!open) return
        function handleClick(e: MouseEvent) {
            const target = e.target as Node
            if (triggerRef?.current?.contains(target)) return
            if (panelRef.current && !panelRef.current.contains(target)) {
                onClose()
            }
        }
        document.addEventListener("mousedown", handleClick)
        return () => {
            document.removeEventListener("mousedown", handleClick)
        }
    }, [open, onClose, triggerRef])

    // ESC 关闭
    useEffect(() => {
        if (!open) return
        function handleKey(e: KeyboardEvent) {
            if (e.key === "Escape") onClose()
        }
        document.addEventListener("keydown", handleKey)
        return () => document.removeEventListener("keydown", handleKey)
    }, [open, onClose])

    const handleSelect = useCallback(
        (emoji: string) => {
            onSelect(emoji)
            // 不关闭，允许连续选择
        },
        [onSelect],
    )

    if (!open || !pos) return null

    const category = EMOJI_CATEGORIES[activeCategory]

    const flip = pos.flip

    return createPortal(
        <div
            ref={panelRef}
            className="fixed w-[320px] z-[9999] animate-in fade-in zoom-in-95 duration-150"
            style={{
                left: pos.left,
                ...(flip
                    ? { bottom: window.innerHeight - pos.top }
                    : { top: pos.top }),
                transformOrigin: flip ? "bottom left" : "top left",
            }}
        >
            {/* 向下弹出时：箭头在顶部朝上 ▲ */}
            {!flip && (
                <div className="ml-3 h-[9px]">
                    <div
                        className="absolute top-0 ml-0 w-0 h-0"
                        style={{
                            borderLeft: "8px solid transparent",
                            borderRight: "8px solid transparent",
                            borderBottom: "9px solid color-mix(in oklch, oklch(var(--border)) 40%, transparent)",
                        }}
                    />
                    <div
                        className="absolute top-[2px] -ml-[7px] left-[calc(0.75rem+8px)] w-0 h-0"
                        style={{
                            borderLeft: "7px solid transparent",
                            borderRight: "7px solid transparent",
                            borderBottom: "8px solid color-mix(in oklch, oklch(var(--card)) 85%, transparent)",
                        }}
                    />
                </div>
            )}
            <div
                className="rounded-2xl overflow-hidden"
                style={{
                    background: "color-mix(in oklch, oklch(var(--card)) 85%, transparent)",
                    backdropFilter: "blur(20px) saturate(180%)",
                    WebkitBackdropFilter: "blur(20px) saturate(180%)",
                    border: "1px solid color-mix(in oklch, oklch(var(--border)) 40%, transparent)",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
                }}
            >
                {/* 分类标签 */}
                <div className="flex items-center gap-0.5 px-2 pt-2 pb-1 border-b border-border/20 overflow-x-auto">
                    {EMOJI_CATEGORIES.map((cat, idx) => (
                        <button
                            key={cat.label}
                            type="button"
                            onClick={() => setActiveCategory(idx)}
                            className={`shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-base transition-colors ${idx === activeCategory
                                ? "bg-primary/15 scale-110"
                                : "hover:bg-foreground/[0.06]"
                                }`}
                            title={cat.label}
                        >
                            {cat.icon}
                        </button>
                    ))}
                </div>

                {/* Emoji 面板 */}
                <div className="px-2 py-2">
                    <p className="text-micro text-muted-foreground/50 px-1 mb-1.5">{category.label}</p>
                    <div className="grid grid-cols-8 gap-0.5 max-h-[220px] overflow-y-auto">
                        {category.emojis.map((emoji, i) => (
                            <button
                                key={`${emoji}-${i}`}
                                type="button"
                                onClick={() => handleSelect(emoji)}
                                className="w-9 h-9 flex items-center justify-center rounded-lg text-lg hover:bg-foreground/[0.08] active:scale-90 transition-all duration-100"
                            >
                                {emoji}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* 向上弹出时：箭头在底部朝下 ▼ */}
            {flip && (
                <div className="ml-3 h-[9px] relative">
                    <div
                        className="absolute bottom-0 left-0 w-0 h-0"
                        style={{
                            borderLeft: "8px solid transparent",
                            borderRight: "8px solid transparent",
                            borderTop: "9px solid color-mix(in oklch, oklch(var(--border)) 40%, transparent)",
                        }}
                    />
                    <div
                        className="absolute bottom-[2px] -left-[7px] ml-[8px] w-0 h-0"
                        style={{
                            borderLeft: "7px solid transparent",
                            borderRight: "7px solid transparent",
                            borderTop: "8px solid color-mix(in oklch, oklch(var(--card)) 85%, transparent)",
                        }}
                    />
                </div>
            )}
        </div>,
        document.body,
    )
}
